import React, { useState } from 'react';
import {
  Box,
  Grid,
  TextField,
  Typography,
  Card,
  CardContent,
  Divider,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Button,
  Chip,
  Select,
  MenuItem,
  InputLabel,
  FormGroup,
  Checkbox,
  Slider
} from '@mui/material';
import { 
  WorkHistory, 
  CheckCircle, 
  CleaningServices,
  Schedule,
  LocationOn,
  Assignment
} from '@mui/icons-material';


const ExperienceStep = ({ formData, updateFormData, errors }) => {
  const [customService, setCustomService] = useState('');

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    updateFormData({ [field]: value });
  };

  const handleRadioChange = (field) => (event) => {
    const value = event.target.value === 'true';
    updateFormData({ [field]: value });
  };

  const handleCheckboxChange = (field) => (event) => {
    const isChecked = event.target.checked;
    const currentServices = formData.servicesOffered || [];
    
    if (isChecked) {
      updateFormData({ 
        servicesOffered: [...currentServices, field] 
      });
    } else {
      updateFormData({ 
        servicesOffered: currentServices.filter(service => service !== field) 
      });
    }
  };

  const handleSliderChange = (field) => (event, value) => {
    // Ensure maxTravelDistance is always a number
    const numericValue = field === 'maxTravelDistance' ? Number(value) || 15 : value;
    updateFormData({ [field]: numericValue });
  };



  const addCustomService = () => {
    if (customService.trim()) {
      const currentServices = formData.servicesOffered || [];
      updateFormData({ 
        servicesOffered: [...currentServices, customService.trim()] 
      });
      setCustomService('');
    }
  };

  const removeService = (serviceToRemove) => {
    const currentServices = formData.servicesOffered || [];
    updateFormData({ 
      servicesOffered: currentServices.filter(service => service !== serviceToRemove) 
    });
  };



  const standardServices = [
    'General House Cleaning',
    'Deep Cleaning',
    'Move-in/Move-out Cleaning',
    'Post-Construction Cleaning',
    'Office Cleaning',
    'Apartment Cleaning',
    'Window Cleaning',
    'Carpet Cleaning',
    'Upholstery Cleaning',
    'Kitchen Deep Clean',
    'Bathroom Deep Clean',
    'Organizing Services'
  ];



  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Experience & Services
      </Typography>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
        Tell us about your cleaning experience and the services you can provide.
      </Typography>

      {/* Experience Level Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WorkHistory color="primary" /> Professional Experience
          </Typography>
          <Divider sx={{ mb: 3 }} />
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label="Years of Professional Cleaning Experience"
                placeholder="0"
                value={formData.yearsOfExperience || ''}
                onChange={handleChange('yearsOfExperience')}
                error={!!errors.yearsOfExperience}
                helperText={errors.yearsOfExperience || 'Enter the number of years you have been working as a professional cleaner'}
                inputProps={{
                  min: 0,
                  max: 50,
                  step: 1
                }}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Describe Your Experience"
                placeholder="Tell us about your cleaning experience, any special training, certifications, or notable clients you've worked with..."
                value={formData.experienceDescription || ''}
                onChange={handleChange('experienceDescription')}
                error={!!errors.experienceDescription}
                helperText={errors.experienceDescription || 'Help us understand your background and strengths'}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Services Offered Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CleaningServices color="primary" /> Services You Can Provide
          </Typography>
          <Divider sx={{ mb: 3 }} />
          
          <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
            Select all the cleaning services you are comfortable providing. You can always add more later.
          </Typography>

          <Grid container spacing={2}>
            {standardServices.map((service) => (
              <Grid item xs={12} sm={6} md={4} key={service}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={(formData.servicesOffered || []).includes(service)}
                      onChange={handleCheckboxChange(service)}
                    />
                  }
                  label={service}
                />
              </Grid>
            ))}
          </Grid>

          {/* Custom Service Addition */}
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Add Custom Service
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
              <TextField
                size="small"
                label="Custom Service"
                value={customService}
                onChange={(e) => setCustomService(e.target.value)}
                placeholder="e.g., Pet Hair Removal, Garage Cleaning"
                sx={{ minWidth: 200 }}
              />
              <Button 
                variant="outlined" 
                onClick={addCustomService}
                disabled={!customService.trim()}
              >
                Add
              </Button>
            </Box>
          </Box>

          {/* Selected Services Display */}
          {formData.servicesOffered && formData.servicesOffered.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                Selected Services ({formData.servicesOffered.length})
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {formData.servicesOffered.map((service) => (
                  <Chip
                    key={service}
                    label={service}
                    color="primary"
                    variant="outlined"
                    onDelete={() => removeService(service)}
                    icon={<CheckCircle />}
                  />
                ))}
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Work Preferences Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Schedule color="primary" /> Work Preferences
          </Typography>
          <Divider sx={{ mb: 3 }} />
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom>
                Maximum Travel Distance (miles)
              </Typography>
              <Box sx={{ px: 2 }}>
                <Slider
                  value={Number(formData.maxTravelDistance) || 15}
                  onChange={handleSliderChange('maxTravelDistance')}
                  min={5}
                  max={50}
                  step={5}
                  marks={[
                    { value: 5, label: '5mi' },
                    { value: 15, label: '15mi' },
                    { value: 25, label: '25mi' },
                    { value: 50, label: '50mi' }
                  ]}
                  valueLabelDisplay="auto"
                  valueLabelFormat={(value) => `${value} miles`}
                />
                <Typography variant="body2" color="textSecondary" sx={{ mt: 1, textAlign: 'center' }}>
                  {Number(formData.maxTravelDistance) || 15} miles from your location
                </Typography>
              </Box>
            </Grid>



            <Grid item xs={12}>
              <FormControl error={!!errors.hasReliableTransportation} required>
                <FormLabel sx={{ fontWeight: 'bold', mb: 1 }}>
                  Do you have reliable transportation and can travel to client locations?
                </FormLabel>
                <RadioGroup
                  value={formData.hasReliableTransportation === null ? '' : formData.hasReliableTransportation.toString()}
                  onChange={handleRadioChange('hasReliableTransportation')}
                  row
                >
                  <FormControlLabel 
                    value="true" 
                    control={<Radio />} 
                    label="Yes, I have reliable transportation" 
                  />
                  <FormControlLabel 
                    value="false" 
                    control={<Radio />} 
                    label="No, I need assistance with transportation" 
                  />
                </RadioGroup>
                {errors.hasReliableTransportation && (
                  <Typography color="error" variant="caption">
                    {errors.hasReliableTransportation}
                  </Typography>
                )}
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Success Message */}
      <Box sx={{ mt: 3, p: 2, backgroundColor: 'success.light', borderRadius: 1 }}>
        <Typography variant="body2" color="success.dark">
          <Assignment sx={{ verticalAlign: 'middle', mr: 1 }} />
          <strong>Looking Good!</strong> Your experience and service preferences help us match you with the right clients.
        </Typography>
      </Box>
    </Box>
  );
};

export default ExperienceStep;